import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { SketchPicker } from 'react-color';
import { createOrganization, OrganizationType } from "../../components/utils/Organization";
import { ColorPickerField } from "../../components/ColorPickerField";

const ORG_TYPES: OrganizationType[] = ["school", "club", "league"];

export const CreateOrganizationForm: React.FC = () => {
  const [name, setName] = useState("");
  const [type, setType] = useState<OrganizationType>("school");
  const [primaryColor, setPrimaryColor] = useState("#1976d2");
  const [secondaryColor, setSecondaryColor] = useState("#fbc02d");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const id = await createOrganization(
        {
          name,
          type,
          primaryColor,
          secondaryColor,
          logoFile
        }
      );

      setOrgId(id);
    } catch (err: any) {
      setError(err.message || "Failed to create organization.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ maxWidth: 600, margin: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Create Organization
        </Typography>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Organization Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <Box>
            <InputLabel id="org-type-label">Organization Type</InputLabel>
            <Select
              labelId="org-type-label"
              value={type}
              fullWidth
              onChange={(e) => setType(e.target.value as OrganizationType)}
            >
              {ORG_TYPES.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <ColorPickerField
                label="Primary Color"
                color={ primaryColor }
                onChange={ (color)=> {setPrimaryColor(color)} }
            />
          </Box>

          <Box>
          <ColorPickerField
                label="Secondary Color"
                color={ secondaryColor }
                onChange={ (color)=> {setSecondaryColor(color)} }
            />
          </Box>

          <Box>
            <InputLabel>Logo</InputLabel>
            <input type="file" accept="image/*" onChange={handleLogoChange} />
            {logoPreview && (
              <Box mt={2}>
                <img
                  src={logoPreview}
                  alt="Logo Preview"
                  style={{ maxWidth: "100%", maxHeight: 150 }}
                />
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !name}
          >
            {loading ? <CircularProgress size={24} /> : "Create Organization"}
          </Button>

          {orgId && (
            <Typography color="success.main">
              Organization created! ID: {orgId}
            </Typography>
          )}
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
};
